<template>
    <SmuMainMenu :sections="filteredSections" :current-route="route.path" />
    <div class="mt-4">
        <MenuRedirect />
    </div>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/restrict-template-expressions, @typescript-eslint/no-unnecessary-condition */
import type { SmuIconProps } from "@smakecloud/smake-ui";
import { SmuMainMenu } from "@smakecloud/smake-ui";

type Link = {
    name: string;
    beta?: boolean;
    dev?: boolean;
    icon: SmuIconProps;
    permission?: string;
    url: string;
};

type Section = {
    name: string;
    links: Link[];
};

const { t } = useI18n();
const { isAuthorised } = useAuthorisationChecker();
const router = useRouter();
const route = useRoute();
const slug = useSlug();

const sections = ref<Section[]>([
    {
        name: "",
        links: [
            {
                name: t("shop/backend.dashboard.menu.title"),
                icon: {
                    icon: "tachometer-alt",
                    type: "light",
                },
                url: `/${slug.value}/dashboard`,
            },
        ],
    },
    {
        name: t("production.menue.goods"),
        links: [
            {
                name: t("production.stock-items.list.title"),
                icon: {
                    icon: "inventory",
                    type: "light",
                },
                url: `/${slug.value}/inventory`,
            },
        ],
    },
    {
        name: t("eloquent.attributes.tenant.settings"),
        links: [
            {
                name: t("eloquent.attributes.production_method.title"),
                icon: {
                    icon: "gem",
                    type: "light",
                },
                url: `/${slug.value}/production-methods`,
            },
            {
                name: t("production.settings.basics.title"),
                icon: {
                    icon: "cogs",
                    type: "light",
                },
                url: `/${slug.value}/settings`,
            },
            {
                name: t("eloquent.attributes.remote_storage.title", 2),
                icon: {
                    icon: "server",
                    type: "light",
                },
                url: `/${slug.value}/remote-storages`,
            },
            {
                name: t(
                    "eloquent.attributes.tenant.export_settings.production_export",
                ),
                icon: {
                    icon: "file-export",
                    type: "light",
                },
                url: `/${slug.value}/production-export`,
            },
        ],
    },
]);

const filteredSections = computed(() =>
    sections.value
        .filter((section) =>
            section.links.some((link) => {
                const resolvedRoute = router.resolve(link.url);

                return isAuthorised({
                    dev: resolvedRoute.meta.dev,
                    feature: resolvedRoute.meta.feature,
                    permission: resolvedRoute.meta.permission,
                });
            }),
        )
        .map((section) => ({
            ...section,
            links: section.links.filter((link) => {
                const resolvedRoute = router.resolve(link.url);

                return isAuthorised({
                    dev: resolvedRoute.meta.dev,
                    feature: resolvedRoute.meta.feature,
                    permission: resolvedRoute.meta.permission,
                });
            }),
        })),
);

const productionProductionStore = useProductionProductionStore();

const { data: productionProductionMethods } = storeToRefs(
    productionProductionStore,
);

const productionMethods = computed<Link[]>(
    () =>
        productionProductionMethods.value?.data
            .filter((method) => method.is_using_presorting)
            .map((method) => ({
                name:
                    method.production_method.display_name ??
                    method.production_method_handle,
                icon: {
                    icon:
                        method.production_method_handle === "dtf"
                            ? method.production_method_handle
                            : "container-storage",
                    type:
                        method.production_method_handle === "dtf"
                            ? "kit"
                            : "light",
                },
                permission: "manage presorting",
                url: `/${slug.value}/presorting/${method.id}`,
            })) ?? [],
);

watchEffect(() => {
    if (!productionMethods.value) {
        return;
    }

    let section = sections.value.find(
        (section) => section.name === t("helpers.common.presorting"),
    );

    if (!section) {
        section = {
            name: t("helpers.common.presorting"),
            links: [],
        };
        sections.value.push(section);
    }

    for (const newLink of productionMethods.value) {
        const hasLink = section.links.some(
            (existingLink) => existingLink.name === newLink.name,
        );

        if (!hasLink) {
            section.links.push(newLink);
        }
    }
});
</script>
